import React, { createContext, useCallback, useContext, useEffect, type MutableRefObject, type ReactNode } from 'react';

type RefreshFn = (() => void | Promise<void>) | null;

interface PageRefreshContextValue {
  register: (fn: RefreshFn) => void;
}

const PageRefreshContext = createContext<PageRefreshContextValue | null>(null);

interface PageRefreshProviderProps {
  refreshRef: MutableRefObject<RefreshFn>;
  children: ReactNode;
}

/** Lets each page register its own reload handler for global pull-to-refresh. */
export function PageRefreshProvider({ refreshRef, children }: PageRefreshProviderProps) {
  const register = useCallback(
    (fn: RefreshFn) => {
      refreshRef.current = fn;
    },
    [refreshRef],
  );

  return (
    <PageRefreshContext.Provider value={{ register }}>
      {children}
    </PageRefreshContext.Provider>
  );
}

/** Register a page-level refresh callback; cleared automatically on unmount. */
export function usePageRefresh(refreshFn: () => void | Promise<void>, deps: React.DependencyList): void {
  const ctx = useContext(PageRefreshContext);

  useEffect(() => {
    if (!ctx) return;
    ctx.register(refreshFn);
    return () => ctx.register(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
